import { useCallback } from 'react';

import {
  UseUpdateEmailNotificationParams,
  UseUpdateEmailNotificationReturnType,
} from './useUpdateEmailNotification.decl';

import { Reference, StoreObject } from '@apollo/client';
import {
  DisableEmailNotificationMutation,
  DisableEmailNotificationMutationVariables,
} from '@generated/DisableEmailNotificationMutation';
import {
  EnableEmailNotificationMutation,
  EnableEmailNotificationMutationVariables,
} from '@generated/EnableEmailNotificationMutation';
import {
  UpdateEmailNotificationFrequencyMutation,
  UpdateEmailNotificationFrequencyMutationVariables,
} from '@generated/UpdateEmailNotificationFrequencyMutation';
import { DISABLE_EMAIL_NOTIFICATION_MUTATION } from '@graphql/mutations/DisableEmailNotificationMutation';
import { ENABLE_EMAIL_NOTIFICATION_MUTATION } from '@graphql/mutations/EnableEmailNotificationMutation';
import { UPDATE_EMAIL_NOTIFICATION_FREQUENCY_MUTATION } from '@graphql/mutations/UpdateEmailNotificationFrequencyMutation';
import { getTrackSettingsProperties } from '@helpers/tracker.helpers';
import { useGlobalData } from '@hooks/useGlobalData/useGlobalData';
import { useGraphMutationWithFeedback } from '@hooks/useMutation';
import { useTranslation } from 'react-i18next';

/**
 * Hook to update email notifications given a user's id and email notifications.
 * @param useUpdateEmailNotificationParams - destructured parameter
 * @returns an object holding functions responsible for updating email notifications, and its states
 */
export function useUpdateEmailNotification({
  user,
}: UseUpdateEmailNotificationParams): UseUpdateEmailNotificationReturnType {
  const { t } = useTranslation();
  const { currentUser } = useGlobalData();

  const isOwnUser = currentUser.ID === user.ID;

  const [enableEmailNotification, { loading: isEnableLoading }] = useGraphMutationWithFeedback<
    EnableEmailNotificationMutation,
    EnableEmailNotificationMutationVariables
  >(ENABLE_EMAIL_NOTIFICATION_MUTATION, { type: 'snackbar' });
  const [disableEmailNotification, { loading: isDisableLoading }] = useGraphMutationWithFeedback<
    DisableEmailNotificationMutation,
    DisableEmailNotificationMutationVariables
  >(DISABLE_EMAIL_NOTIFICATION_MUTATION, { type: 'snackbar' });
  const [updateEmailNotification, { loading: isUpdateLoading }] = useGraphMutationWithFeedback<
    UpdateEmailNotificationFrequencyMutation,
    UpdateEmailNotificationFrequencyMutationVariables
  >(UPDATE_EMAIL_NOTIFICATION_FREQUENCY_MUTATION, { type: 'snackbar' });

  const toggleTurnOnEmailNotification = useCallback(
    async (name: string) => {
      await enableEmailNotification(
        {
          variables: {
            agentID: user.ID,
            input: {
              name,
            },
          },
          update(cache, { data }) {
            cache.modify({
              id: cache.identify({ __typename: 'AgentV2', ID: user.ID }),
              fields: {
                emailNotifications(refs, { toReference }) {
                  const {
                    enableEmailNotification: { ID, frequency },
                  } = data!;

                  return [
                    ...refs,
                    toReference({
                      __typename: 'AgentEmailNotification',
                      ID,
                      frequency,
                      name,
                    }),
                  ];
                },
              },
            });
          },
          optimisticResponse: {
            enableEmailNotification: {
              __typename: 'AgentEmailNotification',
              frequency: 'weekly', // default frequency
              name,
              /**
               * This mutation creates a new entry so we don't know the `ID` before the creation is done.
               * We can pass anything as the `ID` in order to use the optimistic UI here
               * because the `name` is the only field we need to update the toggle status.
               * If the mutation succeeds, the cache will be properly updated.
               */
              ID: '',
            },
          },
        },
        {
          successMessage: t(
            'user_profile.notifications.email_notifications.enabled_success_message'
          ),
          trackerProperties: getTrackSettingsProperties({
            event: 'user_settings_edited',
            settingsSection: 'Notifications',
            setting: 'Email notifications',
            field: name,
            action: 'enabled',
            isOwnUser,
          }),
        }
      );
    },
    [enableEmailNotification, isOwnUser, t, user]
  );

  const toggleTurnOffEmailNotification = useCallback(
    async (name: string) => {
      const { ID } = user.emailNotifications!.find(({ name: enName }) => enName === name)!;

      await disableEmailNotification(
        {
          variables: {
            ID,
            agentID: user.ID,
            input: {
              name,
            },
          },
          update(cache) {
            cache.modify({
              id: cache.identify({ __typename: 'AgentV2', ID: user.ID }),
              fields: {
                emailNotifications: (refs, { readField }) =>
                  refs.filter((ref?: StoreObject | Reference) => ID !== readField('ID', ref)),
              },
            });
          },
          optimisticResponse: {
            disableEmailNotification: {
              __typename: 'AgentEmailNotification',
              ID,
              frequency: null,
              name,
            },
          },
        },
        {
          successMessage: t(
            'user_profile.notifications.email_notifications.disabled_success_message'
          ),
          trackerProperties: getTrackSettingsProperties({
            event: 'user_settings_edited',
            settingsSection: 'Notifications',
            setting: 'Email notifications',
            field: name,
            action: 'disabled',
            isOwnUser,
          }),
        }
      );
    },
    [disableEmailNotification, user, t, isOwnUser]
  );

  const updateEmailNotificationFrequency = useCallback(
    async (name: string, frequency: string) => {
      const { ID } = user.emailNotifications!.find(({ name: enName }) => enName === name)!;

      await updateEmailNotification(
        {
          variables: {
            ID,
            agentID: user.ID,
            input: {
              frequency,
              name,
            },
          },
          optimisticResponse: {
            updateEmailNotificationFrequency: {
              __typename: 'AgentEmailNotification',
              ID,
              frequency,
              name,
            },
          },
        },
        {
          successMessage: t(
            'user_profile.notifications.email_notifications.frequency_updated_success_message'
          ),
          trackerProperties: getTrackSettingsProperties({
            event: 'user_settings_edited',
            settingsSection: 'Notifications',
            setting: 'Email notifications',
            field: `${name}_frequency`,
            isOwnUser,
          }),
        }
      );
    },
    [isOwnUser, t, updateEmailNotification, user]
  );

  return {
    isUpdatingEmailNotification: isEnableLoading || isDisableLoading || isUpdateLoading,
    toggleTurnOnEmailNotification,
    toggleTurnOffEmailNotification,
    updateEmailNotificationFrequency,
  };
}
